import React from "react";
import TaskIcon from "./TaskIcon";

/**
 * Ícones das notificações (sino, cards e toasts): SVG monocromático de traço,
 * mesma base do `TaskIcon` (viewBox 24, `currentColor`, 1em por padrão), então
 * herdam cor e tamanho do texto e funcionam nos dois temas.
 * Substituem os emojis que vinham no título (🔔 ⏰ 🤖) e os ícones preenchidos
 * do react-icons usados no sino.
 */
function Svg({ size = "1em", strokeWidth = 1.8, children, ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const BellIcon = (p) => (
  <Svg {...p}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </Svg>
);

export const CheckCircleIcon = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <polyline points="8 12.5 11 15.5 16 9" />
  </Svg>
);

export const CloseIcon = (p) => (
  <Svg {...p}>
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </Svg>
);

export const MentionIcon = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />
  </Svg>
);

export const AutomationIcon = (p) => (
  <Svg {...p}>
    <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
  </Svg>
);

export const ChatIcon = (p) => (
  <Svg {...p}>
    <path d="M4 5h16v11H9.5L5 20v-4H4z" />
    <line x1="8" y1="9" x2="16" y2="9" />
    <line x1="8" y1="12.5" x2="13" y2="12.5" />
  </Svg>
);

export const StarIcon = (p) => (
  <Svg {...p}>
    <polygon points="12 3 14.8 8.8 21 9.6 16.5 14 17.6 20.3 12 17.3 6.4 20.3 7.5 14 3 9.6 9.2 8.8" />
  </Svg>
);

export const ClockIcon = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15.5 14" />
  </Svg>
);

export const CalendarIcon = (p) => (
  <Svg {...p}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <line x1="3.5" y1="10" x2="20.5" y2="10" />
    <line x1="8" y1="3" x2="8" y2="7" />
    <line x1="16" y1="3" x2="16" y2="7" />
  </Svg>
);

export const UserPlusIcon = (p) => (
  <Svg {...p}>
    <circle cx="10" cy="8" r="4" />
    <path d="M2.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="16" y1="11" x2="22" y2="11" />
  </Svg>
);

// Ícone por tipo de notificação (campo `type` vindo do backend). Tipos novos ou
// desconhecidos caem no sino.
const BY_TYPE = {
  mention: MentionIcon,
  automation_notification: AutomationIcon,
  task_comment: ChatIcon,
  task_feedback: StarIcon,
  task_reminder: TaskIcon,
  stage_sla_breach: ClockIcon,
  calendar_event_reminder: CalendarIcon,
  card_assigned: UserPlusIcon,
  whatsapp_conversation_assigned: ChatIcon,
};

export function NotificationTypeIcon({ type, ...rest }) {
  const Icon = BY_TYPE[type] || BellIcon;
  return <Icon {...rest} />;
}

// Tira o(s) emoji(s) do começo do texto (o backend e o usuário podem colocar um
// no título: "🔔 Notificação interna", "⏰ SLA estourado…"); o ícone passa a ser
// o SVG do tipo. Só mexe no início do texto.
const LEADING_EMOJI =
  /^(?:[\p{Extended_Pictographic}\p{Regional_Indicator}‍️⃣\s])+/u;

export function stripLeadingEmoji(text) {
  if (typeof text !== "string") return text;
  const cleaned = text.replace(LEADING_EMOJI, "");
  return cleaned.trim() ? cleaned : text;
}
